"use client"

import type React from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { FieldRenderer } from "@/components/preview/shared/field-renderer"
import { DraggableField } from "./draggable-field"

interface ArrayItemRendererProps {
  itemData: any
  dataIndex: number
  template: any
  itemScope: string
  currentPath: string[]
  editable: boolean
  draggable: boolean
  onFieldChange?: (path: string[], value: any) => void
  onReorderField?: (
    tabIndex: number,
    componentPath: number[],
    fromRow: number,
    fromCol: number,
    toRow: number,
    toCol: number,
  ) => void
  onRemoveField?: (tabIndex: number, componentPath: number[], rowIndex: number, colIndex: number) => void
  renderItem?: (item: any, parentData?: any, parentScope?: string, parentPath?: string[]) => React.ReactNode
  tabIndex?: number
  componentPath?: number[]
  dataSchema: any
  draggedPosition: { row: number; col: number } | null
  dragOverPosition: { row: number; col: number } | null
  onFieldDragStart: (e: React.DragEvent, rowIndex: number, colIndex: number) => void
  onFieldDragOver: (e: React.DragEvent, rowIndex: number, colIndex: number) => void
  onFieldDragLeave: () => void
  onFieldDrop: (e: React.DragEvent, toRow: number, toCol: number) => void
  onFieldDragEnd: () => void
}

export function ArrayItemRenderer({
  itemData,
  dataIndex,
  template,
  itemScope,
  currentPath,
  editable,
  draggable,
  onFieldChange,
  onReorderField,
  onRemoveField,
  renderItem,
  tabIndex,
  componentPath,
  dataSchema,
  draggedPosition,
  dragOverPosition,
  onFieldDragStart,
  onFieldDragOver,
  onFieldDragLeave,
  onFieldDrop,
  onFieldDragEnd,
}: ArrayItemRendererProps) {
  console.log("[v0] ArrayItemRenderer:", {
    itemData,
    dataIndex,
    template,
    itemScope,
    currentPath,
    templateKeys: template.keys,
  })

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 1024
  const cardHeader = template.header
    ? template.header
        .replace(/\{\{index\}\}/g, String(dataIndex + 1))
        .replace(/\{\{(\w+)\}\}/g, (match: string, key: string) => {
          return itemData[key] !== undefined ? String(itemData[key]) : match
        })
    : `Item ${dataIndex + 1}`

  const handleChange = (key: string, value: any) => {
    if (onFieldChange) {
      onFieldChange([...currentPath, key], value)
    }
  }

  const handleRemoveField = (rowIndex: number, colIndex: number) => {
    if (tabIndex !== undefined && componentPath && onRemoveField) {
      onRemoveField(tabIndex, componentPath, rowIndex, colIndex)
    }
  }

  const renderNestedTable = (matchingChild: any, key: string) => {
    let dataKey = key
    if (matchingChild.scope) {
      // Extract the property name from the scope (e.g., "#/properties/measurements" -> "measurements")
      const scopeParts = matchingChild.scope.split("/")
      dataKey = scopeParts[scopeParts.length - 1]
    } else if (matchingChild.key) {
      dataKey = matchingChild.key
    }

    const tableData = Array.isArray(itemData[dataKey]) ? itemData[dataKey] : []

    console.log("[v0] renderNestedTable:", {
      dataKey,
      itemData,
      tableData,
      hasData: Array.isArray(itemData[dataKey]),
      matchingChild,
    })

    // Get table keys from child config or infer from schema
    let tableKeys = matchingChild.keys || []

    if (tableKeys.length === 0 && dataKey && dataSchema?.properties?.[itemScope]) {
      const arrayItemSchema = dataSchema.properties[itemScope]?.items
      const nestedTableSchema = arrayItemSchema?.properties?.[dataKey]

      if (nestedTableSchema?.type === "array" && nestedTableSchema.items?.properties) {
        tableKeys = Object.keys(nestedTableSchema.items.properties)
      }
    }

    return (
      <div key={key} className="space-y-2">
        <h4 className="text-sm font-semibold">{matchingChild.header || "Table"}</h4>
        <Table>
          <TableHeader>
            <TableRow>
              {tableKeys.length > 0 ? (
                tableKeys.map((k: string) => (
                  <TableHead key={k}>{k.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}</TableHead>
                ))
              ) : (
                <TableHead>No columns configured</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableData.length > 0 ? (
              tableData.map((row: any, rowIdx: number) => (
                <TableRow key={rowIdx}>
                  {tableKeys.map((k: string) => (
                    <TableCell key={k}>{row[k] !== undefined && row[k] !== null ? String(row[k]) : "-"}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableKeys.length || 1} className="text-center text-muted-foreground">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    )
  }

  const getFieldType = (key: string) => {
    if (!dataSchema?.properties?.[itemScope]?.items?.properties?.[key]) {
      return { type: "text" }
    }

    const fieldSchema = dataSchema.properties[itemScope].items.properties[key]
    return {
      type: fieldSchema.type || "text",
      options: fieldSchema.enum || undefined,
    }
  }

  const styles = isMobile ? {gridTemplateColumns: '1fr'} : {gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'}

  return (
    <Card>
      <CardHeader>
        <CardTitle>{cardHeader}</CardTitle>
      </CardHeader>
      <CardContent>
        {template.keys && template.keys.length > 0 ? (
          <div className="space-y-4">
            {template.keys.map((keyOrRow: string | string[], rowIndex: number) => {
              const isRow = Array.isArray(keyOrRow)
              const keys = isRow ? keyOrRow : [keyOrRow]

              if (isRow) {
                return (
                  <div
                    key={rowIndex}
                    className="grid gap-4"
                    style={styles}
                  >
                    {keys.map((key: string, colIndex: number) => {
                      const isDragging = draggedPosition?.row === rowIndex && draggedPosition?.col === colIndex
                      const isDropTarget = dragOverPosition?.row === rowIndex && dragOverPosition?.col === colIndex

                      const fieldValue = itemData[key]
                      const fieldType = getFieldType(key)

                      return (
                        <DraggableField
                          key={key}
                          rowIndex={rowIndex}
                          colIndex={colIndex}
                          draggable={draggable && onReorderField !== undefined}
                          isDropTarget={isDropTarget}
                          onDragStart={onFieldDragStart}
                          onDragOver={onFieldDragOver}
                          onDragLeave={onFieldDragLeave}
                          onDrop={onFieldDrop}
                          onDragEnd={onFieldDragEnd}
                          onRemove={
                            draggable && onRemoveField ? () => handleRemoveField(rowIndex, colIndex) : undefined
                          }
                        >
                          <FieldRenderer
                            fieldKey={key}
                            value={fieldValue}
                            fieldType={fieldType}
                            editable={editable}
                            onChange={(value) => handleChange(key, value)}
                          />
                        </DraggableField>
                      )
                    })}
                  </div>
                )
              }

              // Single field row
              const key = keys[0]
              const fieldValue = itemData[key]
              const fieldType = getFieldType(key)

              return (
                <div key={rowIndex}>
                  <FieldRenderer
                    fieldKey={key}
                    value={fieldValue}
                    fieldType={fieldType}
                    editable={editable}
                    onChange={(value) => handleChange(key, value)}
                  />
                </div>
              )
            })}
          </div>
        ) : null}

        {template.children && template.children.length > 0 && (
          <div className="mt-4 space-y-4">
            {template.children.map((child: any, childIndex: number) => {
              if (child.type === "table") {
                const childKey = child.key || child.scope?.split("/").pop()
                return renderNestedTable(child, childKey)
              } else if (renderItem) {
                return <div key={childIndex}>{renderItem(child, itemData, itemScope, currentPath)}</div>
              }
              return null
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

